import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WizardNavbar from './components/WizardNavbar';
import usePreviousValue from '../../hooks/usePreviousValue';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useSearchPerformed from '../../hooks/useSearchPerformed';
import { getParamFromUrl, isEntryFlow } from '../../utilities/urlResolver';
import { fetchDynamicSteps, getWizardLandingStep, setDataV2, setStep } from './onboardingSlice';
import { ONBOARDING_STEP_LOADED_EVENT, ONBOARDING_START_WIZARD_V2_EVENT, ONBOARDING_LANDING_STEP } from '../../constants/analyticEvents';
import { hideTwigSkeletons } from '../../utilities/helpers';
import { pushEvent } from '../../utilities/analytics';
import { buildSearch, getWizardV2State, transformWizardDataToFilters } from './wizardService';
import useWizardSteps from './useWizardSteps';
import FadeTransition from '../../components/animations/FadeTransition';
import WelcomeStep from './steps/WelcomeStep';
import WizardSlider from './WizardSlider';
import WizardSplashScreen from './components/WizardSplashScreen';
import abTesting from '../../services/abTesting';
import { useAppSelector } from '../../hooks/hooks';
import { selectFilterData } from '../mainFilter/selectors';
import API from '../../api';
import { setData } from '../user/userSlice';

const WizardV2 = () => {
  const dispatch = useDispatch();
  const [userProfile, setUserProfile] = useState(null);

  const { searchEngine } = useAppSelector(selectFilterData);
  const { stepsToShow } = getWizardV2State();
  const { show, currentStep, stepNames, formV2: form } = useSelector((state) => state.onboarding);
  const { entryFlowParams } = useSelector(state => state.user);
  const currentStepIndex = stepNames.findIndex(step => step === currentStep);
  const previousStepIndex = usePreviousValue(currentStepIndex);
  const slideDirection = useMemo(() => (previousStepIndex <= currentStepIndex ? 'slide-forward' : 'slide-backward'), [currentStepIndex, previousStepIndex]);
  const showSplashScreen = abTesting.splashScreenBeforeWhoStep && (abTesting.wizardLandingStepAlternative || abTesting.aiAssistant);

  const [setAppTimeout] = useSearchPerformed();
  const stepsList = useWizardSteps();
  useBodyScrollLock({ condition: show, mobileOnly: true });

  // don't show dots in the step navigation for number of passengers and age
  const stepsNamesForNav = stepNames.filter((step) => (step !== 'adults' && step !== 'children' && step.indexOf('childAges') === -1));
  const currentStepIndexForNav = stepsNamesForNav.findIndex(step => {
    if (currentStep === 'adults' || currentStep === 'children' || (typeof currentStep !== 'number' && currentStep.indexOf('childAges') > -1)) {
      return step === 'who';
    }
    return step === currentStep;
  });

  window.customWelcomeScreenProceed = () => {
    hideTwigSkeletons();

    if (window.pr_welcome) {
      pushEvent(ONBOARDING_START_WIZARD_V2_EVENT, { welcomePresets: window.pr_welcomeScreenToLoad });
    } else {
      pushEvent(ONBOARDING_START_WIZARD_V2_EVENT);
    }
  };

  useEffect(() => {
    if (searchEngine.fromwhere) {
      const startFromwhere = searchEngine.fromwhere.map(dep => ({
        ...dep,
        active: false
      }));
      dispatch(setDataV2({ departure: startFromwhere }));
    }
  }, [dispatch, searchEngine.fromwhere])

  useEffect(() => {
    if (show) {
      if (abTesting.mobileHomepageScrollable) {
        document.querySelector('body').classList.add('mobile-wizzard-active-mobhp');
      } else {
        document.querySelector('body').classList.add('mobile-wizzard-active');
      }
    }

    const handlePopState = () => {
      const currentStep = getParamFromUrl('step');
      if (currentStep >= 0) {
        dispatch(setStep(currentStep));
      }
    }

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch, show]);

  useEffect(() => {
    if (!show) {
      document.querySelector('body').classList.remove('mobile-wizzard-active-mobhp');
      document.querySelector('body').classList.remove('mobile-wizzard-active');
      localStorage.setItem('onboardingCompleted', true);
    }
    if (show) {
      pushEvent(ONBOARDING_LANDING_STEP, getWizardLandingStep());
      document.dispatchEvent(new CustomEvent('wizard loaded'));
      if (window.location.search.indexOf('wizardWidget=true') > -1) {
        window.parent.postMessage({ wizardLoaded: true }, '*');
      }

      if ((abTesting.wizardLandingStep === 'who' || abTesting.wizardLandingStep === 'who2' || abTesting.wizardLandingStepAlternative) && !isEntryFlow('link-presets-welcome') && abTesting.mobileHomepageScrollable) {
        document.querySelector('body').classList.remove('mobile-wizzard-active-mobhp');
        document.querySelector('body').classList.add('mobile-wizzard-active');
      }
    }
  }, [show]);

  useEffect(() => {
    if (userProfile) {
      dispatch(fetchDynamicSteps({ profileId: userProfile.id }));
    }
  }, [dispatch, userProfile])

  useEffect(() => {
    const getUserProfile = async () => {
      const data = buildSearch(form, entryFlowParams);
      const params = transformWizardDataToFilters(data);
      const userProfile = await API.getUserProfile(params);
      setUserProfile(userProfile?.data?.searchProfileData);
      dispatch(setData({ key: 'searchProfile', value: userProfile?.data?.searchProfileData }));
    }

    if (currentStep === 'hostingLevelPref' && slideDirection === 'slide-forward' && abTesting.userPrefEnabled) {
      getUserProfile();
    }
  }, [dispatch, currentStep, form, slideDirection, entryFlowParams])

  useEffect(() => {
    if (show) {
      const stepName = currentStep;
      if (stepName === 'who') {
        setTimeout(() => {
          if (window.pr_welcome) {
            pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
              step: currentStepIndex,
              stepName,
              stepsToShow,
              skipAvailable: !!localStorage.getItem('onboardingCompleted'),
              welcomePresets: window.pr_welcomeScreenToLoad,
            });
          } else {
            pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
              step: currentStepIndex,
              stepName,
              stepsToShow,
              skipAvailable: !!localStorage.getItem('onboardingCompleted'),
            });
          }
        }, 500);
      } else if (stepName === 'email') {
        if (window.pr_welcome) {
          pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
            step: currentStepIndex,
            stepName,
            stepsToShow,
            checkboxEnabled: window.holidayFinder.abTest?.emailStepWithMarketing,
            skipAvailable: !!localStorage.getItem('onboardingCompleted'),
            welcomePresets: window.pr_welcomeScreenToLoad,
          });
        } else {
          pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
            step: currentStepIndex,
            stepName,
            stepsToShow,
            checkboxEnabled: window.holidayFinder.abTest?.emailStepWithMarketing,
            skipAvailable: !!localStorage.getItem('onboardingCompleted'),
          });
        }
      } else {
        if (window.pr_welcome) {
          pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
            step: currentStepIndex,
            stepName,
            stepsToShow,
            skipAvailable: !!localStorage.getItem('onboardingCompleted'),
            welcomePresets: window.pr_welcomeScreenToLoad,
          });
        } else {
          pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
            step: currentStepIndex,
            stepName,
            stepsToShow,
            skipAvailable: !!localStorage.getItem('onboardingCompleted'),
          });
        }
        if (['loading', 'loading-ai'].includes(stepName)) {
          setAppTimeout(4000);
        }
      }
    }
  }, [dispatch, currentStep]);

  if (show && currentStep === 'welcome') {
    return <WelcomeStep />;
  }

  return (
    <FadeTransition in={show} unmountOnExit>
      <div className="wizard-layout">
        <WizardNavbar
          steps={stepsNamesForNav.length - 1}
          slideDirection={slideDirection}
          stepName={stepsNamesForNav[currentStepIndexForNav]}
          currentStepIndex={currentStepIndexForNav}
        />
        <WizardSlider>
          {stepsList[currentStepIndex]?.component}
        </WizardSlider>
        {showSplashScreen && <WizardSplashScreen />}
      </div>
    </FadeTransition>
  );
};

export default WizardV2;
